import type { DistribuicaoMensal, Parametros } from '@favo/core';

import { formatarNumero, SEM_VALOR } from '@/design/formato';

import { formatarVolume } from './grandezas';

/* O texto do laudo que mais de um bloco precisa dizer igual.
 *
 * Ficam aqui as frases COMPARTILHADAS, não todas: prosa que só um bloco
 * escreve mora no bloco. O critério é o de sempre, um enunciado só sai daqui
 * quando dois lugares o repetiriam, porque é aí que eles divergem com o
 * tempo. */

export const MES = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
];

/** Qual ETAPA preenche o dado que falta. Nunca "dados insuficientes" seco. */
export function faltaPreencher(p: Parametros): string {
  if (!(p.ticket > 0)) return 'Informe o valor de venda em Dados do projeto.';
  if (!(p.V12 > 0)) return 'Informe o volume em Sazonalidade ou Indicadores.';
  return 'Dado indisponível.';
}

export function rotuloCobertura(p: Parametros): string {
  return p.coberturaSaz === 1 ? '1 mês' : `${formatarNumero(p.coberturaSaz)} meses`;
}

export function motivoCurvaUniforme(p: Parametros): string {
  if (p.temSazonalidade && p.coberturaSaz < 12) {
    return `a curva usada é uniforme, porque o histórico cobre ${rotuloCobertura(p)} e não dá para saber a forma do ano inteiro.`;
  }
  return 'a curva usada é uniforme, porque não há perfil sazonal com projetos mês a mês.';
}

/** O volume, com a projeção rotulada como projeção quando a cobertura é parcial. */
export function notaDeVolume(p: Parametros): string {
  const volume = formatarVolume(p.V12);
  if (p.temSazonalidade && p.coberturaSaz < 12) {
    return `Cerca de ${volume} projetos no ano, projeção a partir de ${rotuloCobertura(p)} de histórico.`;
  }
  if (p.temSazonalidade) return `${volume} projetos no ano, pela sazonalidade de 12 meses.`;
  return `${volume} projetos no ano, pela média mensal informada.`;
}

export function textoEquilibrio(dist: DistribuicaoMensal | null): string {
  if (!dist) return SEM_VALOR;
  if (dist.mesEquilibrio === null) return 'Não atingido';
  return MES[dist.mesEquilibrio];
}

export function notaEquilibrio(
  p: Parametros,
  dist: DistribuicaoMensal | null,
  falta: string,
): string {
  if (!dist) return falta;
  if (dist.mesEquilibrio === null) {
    return 'A margem acumulada no ano não cobre a despesa fixa do ano.';
  }
  const base = `A partir de ${MES[dist.mesEquilibrio]}, as vendas acumuladas já cobriram a despesa fixa do ano. O que entra depois vira lucro.`;
  return dist.uniforme ? `${base} Mês estimado: ${motivoCurvaUniforme(p)}` : base;
}

/** Usada na prosa da curva E no aria-label do gráfico: têm de dizer o mesmo. */
export function fraseEquilibrio(dist: DistribuicaoMensal, contexto: string): string {
  if (dist.mesEquilibrio === null) {
    return `A margem acumulada não cobre a despesa fixa do ano ${contexto}.`;
  }
  const mes = MES[dist.mesEquilibrio];
  const numero = dist.mesEquilibrio + 1;
  return dist.uniforme
    ? `Mês estimado de equilíbrio ${contexto}: ${mes}, o mês ${numero}.`
    : `Equilíbrio ${contexto} em ${mes}, o mês ${numero}.`;
}
